import { Link } from "react-router-dom";
import PageHead from "@/components/PageHead";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  CheckCircle2,
  Sparkles,
  Compass,
  LayoutDashboard,
  FileText,
  ArrowRight,
  Mail,
  Calendar,
  GraduationCap,
} from "lucide-react";

export default function InstitutionThankYou() {
  return (
    <>
      <PageHead
        title="Thank you — Institution interest received | WeKIT™"
        description="We've received your institution's interest. Explore WeKIT Career Clarity 360™ while our team gets in touch."
        path="/institution-thank-you"
        noIndex
      />
      <main className="min-h-screen bg-background">
        <div className="max-w-4xl mx-auto px-6 py-16">
          {/* Hero */}
          <div className="text-center mb-12">
            <div className="mx-auto mb-6 inline-flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
              <CheckCircle2 className="h-9 w-9 text-primary" />
            </div>
            <div className="inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs text-muted-foreground mb-4">
              <Sparkles className="h-3.5 w-3.5" /> Interest received
            </div>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
              Thank you — we've got your details
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              A member of our schools team will reach out within{" "}
              <span className="font-semibold text-foreground">1 working day</span>{" "}
              to schedule your walkthrough. Meanwhile, explore WeKIT™ Career
              Clarity 360 for yourself.
            </p>
          </div>

          {/* Explore cards */}
          <div className="grid gap-4 sm:grid-cols-2 mb-10">
            <Card className="group hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="mb-2 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                  <Compass className="h-5 w-5 text-primary" />
                </div>
                <CardTitle>Try the assessment</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  Take the flagship Career Clarity 360™ assessment (~15 min) and
                  see what your students would experience on day one.
                </p>
                <Button asChild size="sm">
                  <Link to="/start">
                    Start the assessment{" "}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </CardContent>
            </Card>

            <Card className="group hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="mb-2 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                  <LayoutDashboard className="h-5 w-5 text-primary" />
                </div>
                <CardTitle>Preview the principal demo</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  See a live Institution Intelligence™ dashboard, executive
                  brief, and cohort insights — as a principal would.
                </p>
                <Button asChild size="sm" variant="outline">
                  <Link to="/principal-demo-access">
                    Open principal demo{" "}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </CardContent>
            </Card>

            <Card className="group hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="mb-2 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                  <FileText className="h-5 w-5 text-primary" />
                </div>
                <CardTitle>Read the platform docs</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  How the psychometric engine, career mapping, and AI coach work
                  — with our methodology and data sources.
                </p>
                <Button asChild size="sm" variant="outline">
                  <Link to="/docs">
                    Read the docs <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </CardContent>
            </Card>

            <Card className="group hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="mb-2 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                  <GraduationCap className="h-5 w-5 text-primary" />
                </div>
                <CardTitle>Student quickstart</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  The 4-step journey your students will follow — from
                  assessment to personalised report and coach.
                </p>
                <Button asChild size="sm" variant="outline">
                  <Link to="/student-quickstart">
                    See the journey <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Next steps */}
          <Card className="bg-muted/40 border-dashed">
            <CardContent className="p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h3 className="font-semibold mb-1">Want to move faster?</h3>
                <p className="text-sm text-muted-foreground">
                  Book a slot directly with our schools team, or email us your
                  preferred window.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button asChild variant="default" size="sm">
                  <a
                    href="mailto:hello@wekit.ai?subject=Institution%20demo%20request"
                  >
                    <Mail className="mr-2 h-4 w-4" /> Email our team
                  </a>
                </Button>
                <Button asChild variant="outline" size="sm">
                  <Link to="/">
                    <Calendar className="mr-2 h-4 w-4" /> Back to home
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </>
  );
}
